<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields['title'] }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text style="display: flex; flex-direction: column; gap: 24px">
        <div>
          <label class="label-input">Section Title</label>
          <v-text-field
            v-model="fields.title"
            label="Title"
            :rules="[v => !!v || 'Section title is required']"
            required
            hide-details
          />
        </div>
        <!-- Timeline Section -->
        <div>
          <div class="d-flex align-center mb-4">
            <label for="timeline-enabled" class="label-input mr-2 mb-0">Shipping Timeline</label>
            <input type="checkbox" v-model="fields['timeline-enabled']" id="timeline-enabled" />
          </div>

          <div class="group-item">
            <v-row class="mt-1">
              <v-col cols="4">
                <v-text-field
                  v-model="fields['timeline-ordered-text']"
                  label="Ordered Text"
                  :rules="[v => !!v || 'Ordered text is required']"
                  required
                  dense
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="fields['timeline-shipping-text']"
                  label="Shipping Text"
                  :rules="[v => !!v || 'Shipping text is required']"
                  required
                  dense
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="fields['timeline-delivered-text']"
                  label="Delivered Text"
                  :rules="[v => !!v || 'Delivered text is required']"
                  required
                  dense
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-text-field
                  dense
                  v-model="fields['timeline-shipping-days']"
                  label="Shipping Days (e.g., 10)"
                  hint="Number of days from order to shipping completion"
                  type="number"
                  min="1"
                  inputmode="numeric"
                  :rules="[
                    v => !!v || 'Shipping days is required',
                    v => {
                      const num = Number(v);
                      return (
                        (Number.isInteger(num) && num >= 1) || 'Shipping days must be a positive integer (minimum 1)'
                      );
                    },
                  ]"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  dense
                  v-model="fields['timeline-delivery-days']"
                  label="Delivery Days (e.g., 5)"
                  hint="Days after shipping completion"
                  type="number"
                  min="1"
                  inputmode="numeric"
                  :rules="[
                    v => !!v || 'Delivery days is required',
                    v => {
                      const num = Number(v);
                      return (
                        (Number.isInteger(num) && num >= 1) || 'Delivery days must be a positive integer (minimum 1)'
                      );
                    },
                  ]"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Accordion Section -->
        <div>
          <div class="d-flex justify-space-between align-center mb-4">
            <div class="d-flex align-center">
              <label for="accordion-enabled" class="label-input mb-0 mr-2">
                Order Policy ({{ fields['accordion-items'].length }})
              </label>
              <input type="checkbox" v-model="fields['accordion-enabled']" id="accordion-enabled" />
            </div>
            <v-btn small color="primary" @click="handleAddAccordionItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Policy</span>
            </v-btn>
          </div>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(item, i) in fields['accordion-items']" :key="i">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center">
                  <span>{{ item.title }}</span>
                  <v-btn icon @click.stop="handleRemoveAccordionItem(i)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="wrap-accordion-form">
                  <v-text-field
                    outlined
                    v-model="item.title"
                    label="Title"
                    :rules="[v => !!v || 'Accordion item title is required']"
                    required
                  />
                  <label class="sub-label">Content</label>
                  <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                  <!-- <div v-if="!isAccordionContentValid(item.content)" class="error-message"
                      style="color: #ff6c6c; font-size: 12px; margin-top: 4px;">
                      Content is required and must not be empty
                    </div> -->
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <!-- Reviews Slider -->
        <div>
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="d-flex align-center">
              <label for="reviews-enabled" class="label-input mr-2 mb-0"> Reviews </label>
              <input type="checkbox" v-model="fields['reviews-enabled']" id="reviews-enabled" />
            </div>
            <div class="d-flex justify-end align-center mb-2">
              <v-btn small color="primary" @click="handleAddReview">
                <v-icon size="16">mdi-plus</v-icon>
                <span>Add Review</span>
              </v-btn>
            </div>
          </div>
          <div>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, i) in fields['body-review']" :key="'review-' + i">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <div class="reviewer-header">
                      <div class="reviewer-avatar">
                        <img :src="item.avatar" alt="" onerror="this.style.display='none'" />
                      </div>
                      <div class="reviewer-info">
                        <span class="reviewer-name">{{ item.name || '' }}</span>
                        <div @click.stop>
                          <v-rating
                            color="warning"
                            background-color="#c1c1c1"
                            empty-icon="mdi-star-outline"
                            full-icon="mdi-star"
                            half-icon="mdi-star-half-full"
                            hover
                            length="5"
                            size="20"
                            v-model="item.rating"
                          >
                          </v-rating>
                        </div>
                      </div>
                    </div>
                    <v-btn icon @click.stop="handleRemoveReview(i)">
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="wrap-review-form">
                    <v-text-field v-model="item.name" label="Reviewer Name" />
                    <div class="mb-5">
                      <label>Rating</label>
                      <v-rating
                        color="warning"
                        background-color="#c1c1c1"
                        empty-icon="mdi-star-outline"
                        full-icon="mdi-star"
                        half-icon="mdi-star-half-full"
                        hover
                        length="5"
                        size="35"
                        v-model="item.rating"
                      ></v-rating>
                    </div>
                    <div class="mb-5">
                      <label>Avatar Image</label>
                      <UploadImage
                        @callback="obj => handleUploadAvatar(obj, i)"
                        :image="{ imageSrc: item.avatar }"
                        :height="`150`"
                        :typeUpload="'image'"
                        @callbackDelete="handleRemoveAvatar(i)"
                      />
                    </div>
                    <div class="mb-5">
                      <label>Content</label>
                      <ckeditor :editor="editor" v-model="item['content']" :config="editorConfig" />
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
        <div>
          <label class="label-input">Section Settings</label>
          <div class="group-item">
            <SectionPadding
              class="mb-6"
              :fields="fields.sectionPadding"
              :reset-padding="{ topDesktop: 16, bottomDesktop: 0, topMobile: 16, bottomMobile: 0 }"
            />
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Container Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields['background-color']"
                    name="background-color"
                    label="Background Color"
                    default-color="#ffffff"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields['text-color']"
                    name="text-color"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Shipping Timeline Colors</div>
              <v-row>
                <v-col md="4" cols="12">
                  <ColorPicker
                    v-model="fields['timeline-circle-bg-color']"
                    name="timeline-circle-bg-color"
                    label="Circle background"
                    default-color="#000000"
                  />
                </v-col>
                <v-col md="4" cols="12">
                  <ColorPicker
                    v-model="fields['timeline-svg-color']"
                    name="timeline-svg-color"
                    label="SVG color"
                    default-color="#ffffff"
                  />
                </v-col>
                <v-col md="4" cols="12">
                  <ColorPicker
                    v-model="fields['timeline-line-color']"
                    name="timeline-line-color"
                    label="Timeline line"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Policy Item Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields['accordion-hover-color']"
                    name="accordion-hover-color"
                    label="Hover Color"
                    default-color="#f3f4f6"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields['accordion-border-color']"
                    name="accordion-border-color"
                    label="Border Color"
                    default-color="#e5e7eb"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Pagination Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields['pagination-active-color']"
                    name="pagination-active-color"
                    label="Active Color"
                    default-color="#000000"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields['pagination-inactive-color']"
                    name="pagination-inactive-color"
                    label="Inactive Color"
                    default-color="#cccccc"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" :disabled="!isFormValid" @click="handleSave"> Save </v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '@/views/website/products/components/description/common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import productReviewsJson from '@/assets/json/product-reviews.json';
import ColorPicker from './common/color-picker.vue';
import SectionPadding from './common/section-padding.vue';

export default {
  name: 'order-info-section-editor',
  components: {
    UploadImage,
    ColorPicker,
    SectionPadding,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Order Info',
        'text-color': '#000000',
        'background-color': '#ffffff',
        'pagination-active-color': '#000000',
        'pagination-inactive-color': '#cccccc',
        'timeline-enabled': true,
        'timeline-ordered-text': 'Ordered',
        'timeline-shipping-text': 'Shipping',
        'timeline-delivered-text': 'Delivered',
        'timeline-shipping-days': 10,
        'timeline-delivery-days': 5,
        'timeline-circle-bg-color': '#000000',
        'timeline-svg-color': '#ffffff',
        'timeline-line-color': '#000000',
        'accordion-enabled': true,
        'accordion-hover-color': '#f3f4f6',
        'accordion-border-color': '#e5e7eb',
        'accordion-items': [
          {
            title: 'Shipping Rule',
            content:
              '<p><strong>Our Policy:</strong></p><p>1.&nbsp;Shipping costs vary depending on the shipping destination and the number of items that you purchase.&nbsp;The shipping cost will automatically update and display in your total costs.</p><p>2. After you successfully ordered, it takes a few days to process your order and then&nbsp;the packages will be shipped successfully in&nbsp;9 - 14 business days.&nbsp;</p><p><em>For more information, check out the</em><strong><em> Shipping &amp; Delivery </em></strong><em>at our website footer </em></p>',
          },
          {
            title: 'Return & Refund',
            content:
              '<p><strong>Our Guarantee:</strong></p><p>1. You have only&nbsp;<strong>7 days of the arrival date</strong>&nbsp;to&nbsp;report damaged/broken/missing items with photographic evidence. No refunds or exchanges can be offered after this period.</p><p>2. If you are not happy with your purchase for any reason, you can return the package&nbsp;<strong><u>within</u>&nbsp;30 days</strong>&nbsp;from the delivery date.&nbsp;Please contact our customer service before returning, we will try our best to deal with your problem.</p><p><em>For more information, check out the </em><strong><em>Refund &amp; Return Policy </em></strong><em>at our website footer.</em></p>',
          },
        ],
        'reviews-enabled': true,
        'body-review': [],
        // padding: {
        //   top: true,
        //   bottom: false,
        // },
        sectionPadding: {
          topDesktop: 16,
          bottomDesktop: 0,
          topMobile: 16,
          bottomMobile: 0,
        },
      },
    };
  },
  computed: {
    isFormValid() {
      // Check if all required fields are valid
      const titleValid = this.fields.title && this.fields.title.trim().length > 0;

      let timelineValid = true;
      if (this.fields['timeline-enabled']) {
        const shippingDays = Number(this.fields['timeline-shipping-days']);
        const deliveryDays = Number(this.fields['timeline-delivery-days']);

        timelineValid =
          this.fields['timeline-ordered-text'] &&
          this.fields['timeline-ordered-text'].trim().length > 0 &&
          this.fields['timeline-shipping-text'] &&
          this.fields['timeline-shipping-text'].trim().length > 0 &&
          this.fields['timeline-delivered-text'] &&
          this.fields['timeline-delivered-text'].trim().length > 0 &&
          Number.isInteger(shippingDays) &&
          shippingDays >= 1 &&
          Number.isInteger(deliveryDays) &&
          deliveryDays >= 1;
      }

      return titleValid && timelineValid;
    },
  },
  created() {
    this.fields = {
      ...this.fields,
      ...this.sectionEditor.sectionExtract,
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    isAccordionContentValid(content) {
      if (!content) return false;
      // Remove HTML tags and check if there's actual text content
      const textContent = content.replace(/<[^>]*>/g, '').trim();
      return textContent.length > 0;
    },
    handleAddAccordionItem() {
      this.fields['accordion-items'].push({
        title: 'New Policy',
        content: '<p>Enter content here...</p>',
      });
    },
    handleRemoveAccordionItem(index) {
      this.fields['accordion-items'].splice(index, 1);
    },
    handleAddReview() {
      const existingKeys = new Set(this.fields['body-review'].map(r => `${r.name}-${r.avatar}`));

      const candidates = productReviewsJson.slice(0, 10).filter(item => {
        const key = `${item.fullName || ''}-${item.avatar || ''}`;
        return !existingKeys.has(key);
      });

      if (candidates.length === 0) {
        this.$store.dispatch('setMessage', { color: 'warning', dialog: true, content: 'No more reviews to add!' });
        return;
      }

      const randomReview = candidates[Math.floor(Math.random() * candidates.length)];

      this.fields['body-review'].unshift({
        name: randomReview.fullName || ``,
        rating: 5,
        avatar: randomReview.avatar || '',
        content: 'Great product!',
      });
    },
    handleRemoveReview(index) {
      this.fields['body-review'].splice(index, 1);
    },
    handleUploadAvatar(obj, index) {
      if (obj && obj.imageSrc) {
        this.fields['body-review'][index].avatar = obj.imageSrc;
      }
    },
    handleRemoveAvatar(index) {
      this.fields['body-review'][index].avatar = '';
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionOrderInfo(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields['title'],
        html: updateHtml,
      });
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.ck-editor__editable_inline {
  min-height: 200px;
}

.ck-editor__editable {
  min-height: 200px;
}

.sub-label {
  font-size: 14px !important;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  display: block;
}

.wrap-accordion-form {
  .ck-editor__editable {
    min-height: 150px;
  }
}

.reviewer-header {
  display: flex;
  align-items: center;
  gap: 12px;

  .reviewer-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #e0e0e0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      position: relative;
      z-index: 2;
    }

    &::before {
      content: '';
      font-size: 20px;
      color: #999;
      position: absolute;
    }
  }

  .reviewer-name {
    font-size: 16px;
    font-weight: 500;
  }
}
</style>
